import { FC, useEffect, useState } from 'react';
import './StudentProfileForm.scss';
import { useForm, SubmitHandler, FormProvider, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from "@apollo/client";
import { getDecryptedDataWithSecretKey, handleInputWithoutSpaces } from '../../utils/helper';
import FileUpload from '../Popups/FileUpload';
import InputField from '../InputField/InputField';
import ToasterWithMessage from '../toaster';
import { messages, patterns, STUDENT_ROLE, TEACHER_ROLE } from '../../utils/constants';
import images from '../../assets/images';
import { EDIT_TEACHER, UPDATE_STUDENT_PROFILE } from '../../pages/AdminStudents/api/AdminStudentsQuery';
import { useAppSelector } from '../../redux/app/hooks';
import { logout, setRefetchCurrentUser, setSplashvisible } from '../../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonStyle from '../ButtonStyle/ButtonStyle';

interface propsType {
    id: string;
    fullName: string;
    email: string;
    phonenumber: string;
    username: string;
    password: string;
    avatarUrl: string;
    isEdit: boolean;
    displayForm?: boolean;
    setDisplayForm?: (val: boolean) => void;
    isSubmit?: boolean;
    setIsEdit?: (val: boolean) => void;
}

interface IFormInput {
    fullName: string;
    email?: string;
    phonenumber?: string;
    username: string;
    password: string;
}

const schema = yup.object().shape({
    password: yup
        .string()
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength)
});

const StudentProfileForm: FC<propsType> = ({ id, fullName, email, phonenumber, username, password, avatarUrl, isEdit, displayForm, setDisplayForm, isSubmit, setIsEdit }) => {
    const { success, error } = ToasterWithMessage();
    const { solidinterfaceplus, User, uploadIconMobile } = images;
    const [profileImageURL, setProfileImageURL] = useState<string>();
    const [ImageURL, setImageURL] = useState<string>();
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const methods = useForm<IFormInput>({
        resolver: yupResolver(schema) as Resolver<any>,
    });
    const { handleSubmit, setFocus, reset, formState: { errors } } = methods;
    const role = useAppSelector((state) => state.auth.role);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [updateStudentProfile, { loading: updateStudentLoading }] = useMutation(UPDATE_STUDENT_PROFILE, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success(`Profile updated successfully`)
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            if (setIsEdit) {
                setIsEdit(!false);
            }
            if (role === STUDENT_ROLE) {
                dispatch(setRefetchCurrentUser({ refetchCurrentUser: true }));
                navigate('/student-settings');
            }
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [editTeacher, { loading: editTeacherLoading }] = useMutation(EDIT_TEACHER, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success(`Profile updated successfully`);
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            if (setIsEdit) {
                setIsEdit(!false);
            }
            dispatch(setRefetchCurrentUser({ refetchCurrentUser: true }));
            navigate('/teacher-settings');
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    function areValuesDifferent(data: IFormInput) {
        const initialValues = {
            fullName: fullName,
            phonenumber: phonenumber,
            email: email,
            username: username,
            password: getDecryptedDataWithSecretKey(password).originalText
        };
        if(role === TEACHER_ROLE){
            if (data.password !== initialValues.password) return true;
        }else{
            if (data.fullName !== initialValues.fullName) return true;
            if (data.username !== initialValues.username) return true;
            if (data.email !== initialValues.email) return true;
            if (data.phonenumber !== initialValues.phonenumber) return true;
            if (data.password !== initialValues.password) return true;
        }
        

        return false;
    };

    const onSubmit: SubmitHandler<IFormInput> = async data => {       
        if (((areValuesDifferent(data)) || ((profileImageURL !== avatarUrl) && (profileImageURL !== User)))) {
            if (role === STUDENT_ROLE) {
                await updateStudentProfile({
                    variables:
                    {
                        input: {
                            sId: id,
                            fullName: data.fullName.trim(),
                            phoneNumber: data.phonenumber,
                            emailId: data.email,
                            ...(profileImageURL !== User && profileImageURL && { avatarUrl: profileImageURL }),
                            ...(data?.username && (data?.username !== username) && { userName: data.username.trim() }),
                            ...(data?.password && (data?.password !== getDecryptedDataWithSecretKey(password).originalText) && { password: data.password.trim() })
                        }
                    }
                }).then((result)=>{
                    if ((data?.username !== username || data?.password !== getDecryptedDataWithSecretKey(password).originalText) && !result?.errors) {
                        setTimeout(() => {
                            dispatch(logout());
                            dispatch(setSplashvisible());
                        },500);
                    }
                });
            }
            else if (role === TEACHER_ROLE) {
                await editTeacher({
                    variables:
                    {
                        ...(profileImageURL !== User && profileImageURL && { avatarUrl: profileImageURL }),
                        ...(data?.password && (data?.password !== getDecryptedDataWithSecretKey(password).originalText) && { password: data.password.trim() }),
                    }
                }).then((result)=>{
                    if ((data?.password !== getDecryptedDataWithSecretKey(password).originalText) && !result?.errors) {
                        setTimeout(() => {
                            dispatch(logout());
                            dispatch(setSplashvisible());
                        },500);
                    }
                });
            }
        } else if ((!areValuesDifferent(data))){
            error("There is no changes to update");
        }
        else {
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            if (setIsEdit) {
                setIsEdit(false);
            }
            if (role === STUDENT_ROLE) {
                navigate('/student-settings');
            }else{
                navigate('/teacher-settings');
            }
            reset();
        }
    };

    useEffect(() => {
        if (isEdit) {
            setFocus('fullName');
        }
    }, [setFocus]);

    useEffect(() => {
        reset({
            fullName: fullName,
            username: username,
            email: email,
            phonenumber: phonenumber,
            password: getDecryptedDataWithSecretKey(password).originalText
        });
        setProfileImageURL(avatarUrl ? avatarUrl : User);
        setImageURL(avatarUrl ? baseUrl+ avatarUrl : User);

    }, [id]);

    useEffect(() => {
        if (isSubmit) {
            handleSubmit(onSubmit)();
        }
    }, [isSubmit]);

    const handleClickUpload = () => {
        if (isEdit) {
            setIsUploadOpen(true);
        }
    };

    useEffect(() => {
        if (!isUploadOpen) {
            if (profileImageURL === undefined) {
                setProfileImageURL(User);
            }
        }
    }, [isUploadOpen]);

    useEffect(()=>{
            if(profileImageURL !== User && profileImageURL !== undefined){
                setImageURL(baseUrl+profileImageURL)
            }
    },[profileImageURL]);

    return (
        <div className={`StudentProfileFormContainer ${role === STUDENT_ROLE && 'hideOverflow'}`}>
            <div className="adminStudentFormContainer">
                <FormProvider {...methods}>
                    <form className="addStudentForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className={`addStudentParentContainer ${Object.keys(errors).length ? 'hasErrorStyle' : ''}`}>
                            <div className="StudentProfileImage">
                                <div className={`${ImageURL ? 'StudentEditContainer' : 'StudentImageContainer'}`}>
                                    <img className={`${ImageURL ? 'StudentImageProfileStyle' : 'StudentImageDefault'}`} alt='TeacherImage' src={ImageURL} />
                                    <img onClick={handleClickUpload} className="uploadButtonMobile" alt="plus" src={uploadIconMobile} />
                                </div>
                                <div onClick={handleClickUpload} className={`uploadPhoto ${ !isEdit && "defaultCursor"}`}>
                                    <span>Upload photo</span>
                                    <img
                                        className="uploadButton"
                                        alt="plus"
                                        src={solidinterfaceplus}
                                    />
                                </div>
                                {
                                    isUploadOpen ?
                                        <FileUpload type='profile' setUrl={setProfileImageURL} open={isUploadOpen} setOpen={setIsUploadOpen} folderName="images" />
                                        : null
                                }
                            </div>
                            <div className="StudentDetails">
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Full Name</label>
                                    <InputField label="Full Name" name="fullName" classname="subjectStyle" type="text" size="addTeacherField" isDisable={true} />
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Username</label>
                                    <InputField label="Username" name="username" type="text" size="addTeacherField" isDisable={true} />
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Email</label>
                                    <InputField label="Email" name="email" type="text" size="addTeacherField" isDisable={true} onInput={handleInputWithoutSpaces} />
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Phone Number</label>
                                    <InputField label="Phone Number" name="phonenumber" type="text" size="addTeacherField" isDisable={true} onInput={handleInputWithoutSpaces}/>
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Password</label>
                                    <InputField label="Password" name="password" type="password" size="addTeacherField" isDisable={!isEdit} />
                                </div>
                                <div className="StudentNameField saveButtonMobile">
                                    <ButtonStyle paddingLeft='26px' paddingRight='28px' width='calc(100% - 60px)' height='35px' disableButton={updateStudentLoading || editTeacherLoading} text={ isEdit ? "Save" : "Edit"} type="submit" onClick={() => isEdit ? handleSubmit(onSubmit)() : (setIsEdit && setIsEdit(true)) } />
                                </div>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default StudentProfileForm;
